import {
  ALL_MENU_DATA,
  ALL_MENU_OBJECT_DATA,
  billingData,
  hiddenData,
  mapData,
  routeData,
  searchData,
  siteData,
} from 'routers/routes.data';

const featureDates = siteData.featureDates;

/**
 * 성능 최적화를 위한 페이지 조회용 맵입니다.
 * 키는 페이지 키이고, 값은 페이지 객체입니다.
 * @type {Object.<string, Object>}
 */
const PAGE_LOOKUP = ALL_MENU_DATA.reduce((acc, data) => {
  if (data.pages) {
    Object.assign(acc, data.pages);
  }
  return acc;
}, {});

/**
 * 메뉴 구조 내의 문자열 키를 실제 페이지 객체로 재귀적으로 변환합니다.
 * @param {any} menu - 메뉴 아이템 (객체, 배열, 또는 문자열 키)
 * @returns {any} 변환된 메뉴 구조
 */
const resolveMenu = (menu) => {
  if (Array.isArray(menu)) {
    return menu.map((item) => resolveMenu(item));
  } else if (typeof menu === 'object' && menu !== null) {
    const resolved = {};
    for (const key in menu) {
      resolved[key] = resolveMenu(menu[key]);
    }
    return resolved;
  } else if (typeof menu === 'string') {
    // 문자열인 경우 페이지 조회 시도
    return PAGE_LOOKUP[menu] || menu;
  }
  return menu;
};

/**
 * 성능 최적화를 위한 메뉴 데이터 조회용 맵입니다.
 * 메뉴 내의 키들은 실제 페이지 객체로 변환되어 저장됩니다.
 * @type {Object.<string, Array|Object>}
 */
const MENU_LOOKUP = {
  search: resolveMenu(searchData.menu),
  route: resolveMenu(routeData.menu),
  map: resolveMenu(mapData.menu),
  billing: resolveMenu(billingData.menu),
  site: resolveMenu(siteData.menu),
};

/**
 * 키를 사용하여 미리 계산된 맵에서 페이지 정보를 검색합니다.
 * @param {string} key - 페이지 고유 키
 * @returns {Object} 제목, 경로 등을 포함한 페이지 정보 객체. 찾을 수 없는 경우 기본 객체를 반환합니다.
 */
export const getPage = (key) => {
  const page = PAGE_LOOKUP[key];
  if (!page) {
    return { title: key, path: '#' };
  }
  return page;
};

/**
 * 키를 사용하여 메뉴 데이터를 검색합니다.
 * 반환된 메뉴 데이터의 잎(leaf) 노드는 페이지 키 대신 실제 페이지 객체입니다.
 * @param {string} key - 메뉴 카테고리 키 (예: 'search', 'billing')
 * @param {string} [subKey] - (선택 사항) 메뉴 카테고리 내의 하위 키 (예: 'info', 'application')
 * @returns {Array|Object|undefined} 페이지 객체로 구성된 메뉴 데이터 또는 하위 데이터
 */
export const getPageContent = (key, subKey) => {
  const content = MENU_LOOKUP[key];
  if (subKey && content) {
    return content[subKey];
  }
  return content;
};

/**
 * 데이터 객체(파일 단위) 내의 모든 페이지를 순회하며 날짜 정보를 수집합니다.
 * 가장 이른 생성일(minCreate)과 가장 늦은 활동일(maxUpdate)을 반환합니다.
 * @param {Object} data - JSON 데이터 객체
 * @returns {{ minCreate: Date|null, maxUpdate: Date|null }}
 */
export const getDateRangeFromData = (data) => {
  let minCreate = null;
  let maxUpdate = null;

  if (data && data.pages) {
    Object.values(data.pages).forEach((page) => {
      let createDate = null;
      let updateDate = null;

      // 1. Creation Date 파악
      // createAt이 있으면 사용, 없으면 dateKey(Feature 출시일) 사용
      if (page.createAt) {
        createDate = new Date(page.createAt);
      } else if (page.dateKey && featureDates && featureDates[page.dateKey]) {
        createDate = new Date(featureDates[page.dateKey]);
      }

      // 2. Update Date 파악 (가장 최근 활동)
      // updateAt > createAt > dateKey 순으로 고려하지 않고, 존재하는 모든 날짜 중 Max값 찾기
      const dates = [];
      if (page.updateAt) dates.push(new Date(page.updateAt));
      if (page.createAt) dates.push(new Date(page.createAt));
      if (page.dateKey && featureDates && featureDates[page.dateKey]) {
        dates.push(new Date(featureDates[page.dateKey]));
      }

      if (dates.length > 0) {
        // 해당 페이지의 가장 최신 날짜
        updateDate = new Date(Math.max(...dates));
      }

      // Global Min Create 갱신
      if (createDate) {
        if (!minCreate || createDate < minCreate) {
          minCreate = createDate;
        }
      }

      // Global Max Update 갱신
      if (updateDate) {
        if (!maxUpdate || updateDate > maxUpdate) {
          maxUpdate = updateDate;
        }
      }
    });
  }
  return { minCreate, maxUpdate };
};

/**
 * 모든 데이터 소스를 순회하며 tagType이 있는 경우 해당 태그의 타임스탬프 맵을 생성합니다.
 * - createdAt: 해당 태그 그룹 내 가장 오래된 생성일
 * - updatedAt: 해당 태그 그룹 내 가장 최신 활동일 (생성일보다 최신일 경우에만 설정, 같으면 null)
 * @returns {Object} { [tagType]: { createdAt: Date, updatedAt: Date|null } } 형태의 맵
 */
export const getMenuTimestamps = () => {
  const timestamps = {};

  ALL_MENU_DATA.forEach((data) => {
    // 해당 데이터셋의 날짜 범위 계산
    const { minCreate, maxUpdate } = getDateRangeFromData(data);
    if (!minCreate && !maxUpdate) return;

    // 해당 데이터셋에 포함된 tagType 찾기
    if (data.pages) {
      const foundTags = new Set();
      Object.values(data.pages).forEach((page) => {
        if (page.tagType) {
          foundTags.add(page.tagType);
        }
      });

      foundTags.forEach((tag) => {
        // 기존 값과 비교하여 범위 확장 (여러 파일이 동일 태그 공유 시)
        const current = timestamps[tag] || { createdAt: null, updatedAt: null };

        let newMinCreate = current.createdAt;
        let newMaxUpdate = current.updatedAt;

        // Min Create 갱신
        if (minCreate) {
          if (!newMinCreate || minCreate < newMinCreate) {
            newMinCreate = minCreate;
          }
        }

        // Max Update 갱신
        if (maxUpdate) {
          // current.updatedAt이 null일 수 있으므로 주의 (null은 아주 옛날로 취급하거나 별도 처리)
          // 여기서는 Date 객체끼리 비교
          const currentMax = current.updatedAt || current.createdAt; // 기존의 최신 활동일
          const incomingMax = maxUpdate;

          if (!currentMax || incomingMax > currentMax) {
            newMaxUpdate = incomingMax;
          }
        }

        let finalUpdatedAt = newMaxUpdate;
        if (
          newMinCreate &&
          newMaxUpdate &&
          newMinCreate.getTime() === newMaxUpdate.getTime()
        ) {
          finalUpdatedAt = null;
        }

        timestamps[tag] = {
          createdAt: newMinCreate,
          updatedAt: finalUpdatedAt,
        };
      });
    }
  });

  return timestamps;
};

/**
 * (Deprecated) 하위 호환성을 위해 남겨둠. 내부적으로 getDateRangeFromData의 maxUpdate를 반환.
 */
export const getLatestDateFromData = (data) => {
  const { maxUpdate } = getDateRangeFromData(data);
  return maxUpdate;
};

// 필요한 경우 직접 접근을 위해 원본 데이터 내보내기
export {
  billingData,
  mapData,
  routeData,
  searchData,
  siteData,
  featureDates,
  ALL_MENU_DATA as ALL_PAGE_DATA,
  ALL_MENU_OBJECT_DATA,
};

/**
 * 미리 계산된 PAGE_LOOKUP에서 키(key)에 해당하는 페이지 정보를 찾아 반환합니다.
 * @param {string} key 찾으려는 페이지의 키
 * @returns {Object|null} 페이지 정보 객체
 */
export const getPageByKey = (key) => {
  return PAGE_LOOKUP[key] || null;
};

/**
 * 특정 데이터 소스와 메뉴 키를 기반으로 페이지 리스트를 반환합니다.
 * @param {Object} data JSON 데이터 (mapData, routeData 등)
 * @param {string} key menu 객체의 키 (예: '지도', '탐색')
 * @returns {Array} 페이지 정보 리스트
 */
export const getMenuByProperty = (data, key) => {
  const pageKeys = data.menu?.[key] || [];
  return pageKeys
    .map((pageKey) => {
      const page = data.pages[pageKey];
      if (!page) return null;

      return {
        key: pageKey,
        title: page.title,
        path: page.path ?? null,
        link: page.link ?? null,
        description: page.description,
        createAt: page.createAt,
        updateAt: page.updateAt,
        isNewTab: page.isNewTab,
        ...page,
      };
    })
    .filter(Boolean);
};
